/* R148d · Feb 2026 — TaskDateField
 * A wrapper around <Input type="date" | "datetime-local"> that:
 *   1. Handles the empty → first-set case WITHOUT asking for a reason
 *      (previously the parent's `patchTask` unconditionally required a
 *      justification comment).
 *   2. When the previous value is non-empty and the user tries to change
 *      it, opens `TaskDateChangeReasonDialog` so the user can enter a
 *      reason. That reason is sent alongside the patch as
 *      `date_change_reason` and the backend logs it to the task's
 *      activity thread.
 *   3. Cancelling the reason dialog reverts the input to the old value. */
import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import TaskDateChangeReasonDialog from "./TaskDateChangeReasonDialog";

function toInputValue(iso, inputType) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    if (inputType === "date") return iso.slice(0, 10);
    // datetime-local expects YYYY-MM-DDTHH:MM in browser local tz
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function toIsoFromInput(inputVal, inputType) {
  if (!inputVal) return null;
  if (inputType === "date") return inputVal;                              // YYYY-MM-DD
  return new Date(inputVal).toISOString();                                // full ISO
}

const FIELD_LABELS = {
  start_date: "Start date",
  due_date: "Due date",
  actual_start_date: "Actual start",
  actual_end_date: "Actual completion",
};

export default function TaskDateField({ task, field, value, inputType = "date", onCommit, testid }) {
  const [inputVal, setInputVal] = useState(toInputValue(value, inputType));
  const [pendingChange, setPendingChange] = useState(null); // {isoNew, prevInput}
  const originalRef = useRef(value);

  useEffect(() => {
    setInputVal(toInputValue(value, inputType));
    originalRef.current = value;
  }, [value, inputType]);

  const commit = (isoNew, opts = {}) => {
    // Optimistic local update — parent will merge server response.
    onCommit({ [field]: isoNew, ...(opts.reason ? { date_change_reason: opts.reason } : {}) });
  };

  const evaluateCommit = (newInput) => {
    const isoNew = toIsoFromInput(newInput, inputType);
    const prevIso = originalRef.current || null;
    if ((isoNew || null) === (prevIso || null)) return;              // no-op
    // First-time set (previous was blank) → commit without a reason.
    if (!prevIso) {
      commit(isoNew);
      originalRef.current = isoNew;
      return;
    }
    // Real change → open the reason modal.
    setPendingChange({ isoNew, newInputVal: newInput, prevInputVal: toInputValue(prevIso, inputType) });
  };

  const onChange = (e) => {
    const v = e.target.value;
    setInputVal(v);
    // R152 · Feb 2026 — Fire the reason prompt IMMEDIATELY when the user
    // finishes picking a valid date. Prior behaviour waited for blur,
    // which felt slow because the native date-picker often keeps focus
    // in the input after closing.
    //   • For inputType="date" the browser only fires onChange when it
    //     has a complete YYYY-MM-DD (10 chars) so no debounce needed.
    //   • For datetime-local we require 16 chars (YYYY-MM-DDTHH:MM) so
    //     we don't prompt mid-typing.
    if (inputType === "date" && v.length === 10) evaluateCommit(v);
    else if (inputType === "datetime-local" && v.length >= 16) evaluateCommit(v);
  };

  const onBlur = (e) => {
    // onChange handles the common "picked from calendar" path — this is
    // the fallback for edge cases (e.g. user typed a partial value then
    // tabbed away).
    evaluateCommit(e.target.value);
  };

  const confirmChange = (reason) => {
    if (!pendingChange) return;
    commit(pendingChange.isoNew, { reason });
    setPendingChange(null);
  };

  const cancelChange = () => {
    if (!pendingChange) return;
    // Revert input UI to the previous value.
    setInputVal(pendingChange.prevInputVal);
    setPendingChange(null);
  };

  return (
    <>
      <Input
        type={inputType}
        value={inputVal}
        onChange={onChange}
        onBlur={onBlur}
        className="h-8 text-xs"
        data-testid={testid}
      />
      {pendingChange && (
        <TaskDateChangeReasonDialog
          fieldLabel={FIELD_LABELS[field] || field}
          previousValue={toInputValue(originalRef.current, inputType)}
          newValue={pendingChange.newInputVal}
          onConfirm={confirmChange}
          onCancel={cancelChange}
        />
      )}
    </>
  );
}
